import { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { 
  ArrowLeft, 
  Edit, 
  Users,
  GitBranch,
  Lock,
  Gamepad2,
  Zap,
  Puzzle,
  Target,
  Trophy,
  Swords,
  Sparkles,
  Edit3,
  Clock,
  Upload,
  Eye,
  Heart,
  TrendingUp,
  ChevronDown,
  Star,
  BarChart3,
  CheckCircle,
  XCircle,
  Copy,
  ExternalLink,
  Link2,
  Share2
} from 'lucide-react'
import { useProjectsStore } from '../store/useProjectsStore'
import { useToastStore } from '../store/useToastStore'
import { projectsService } from '../services/projectsService'
import SmoothLoadingSpinner from '../components/common/SmoothLoadingSpinner'
import { ProjectEditModal } from '../components/projects/ProjectEditModal'
import { ProjectTransferModal } from '../components/projects/ProjectTransferModal'
import VersionHistoryModal from '../components/common/VersionHistoryModal'
import ConfirmationModal from '../components/common/ConfirmationModal'
import GameOptionsDropdown from '../components/common/GameOptionsDropdown'
import { TeamTag } from '../components/common/TeamTag'
import type { Game } from '../types/api'
import type { VersionAction } from '../components/common/VersionListItem'
import { FILL_GRADIENT, LINE_GRADIENT } from '../constants/gradients'
import { AreaChart, Area, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'


// Game Overview Section Component
const GameOverviewSection = ({ project: _project }: { project: Game }) => {
  const [activeTab, setActiveTab] = useState<string>('overview')

  // Placeholder data for charts
  const platformViewsData = [
    { day: 'Mon', TikTok: 3000, Twitch: 4500, YouTube: 8000 },
    { day: 'Tue', TikTok: 3200, Twitch: 5500, YouTube: 9000 },
    { day: 'Wed', TikTok: 3800, Twitch: 6500, YouTube: 10500 },
    { day: 'Thu', TikTok: 4500, Twitch: 8000, YouTube: 12500 },
    { day: 'Fri', TikTok: 6500, Twitch: 11500, YouTube: 16500 },
    { day: 'Sat', TikTok: 7500, Twitch: 12000, YouTube: 17500 },
    { day: 'Sun', TikTok: 6800, Twitch: 11000, YouTube: 17000 },
  ]

  const peakHoursData = [
    { time: '12am', viewers: 450 },
    { time: '3am', viewers: 300 },
    { time: '6am', viewers: 380 },
    { time: '9am', viewers: 680 },
    { time: '12pm', viewers: 920 },
    { time: '3pm', viewers: 1450 },
    { time: '6pm', viewers: 2380 },
    { time: '9pm', viewers: 2180 },
  ]

  const engagementData = [
    { day: 'Mon', Comments: 50, Gifts: 20, Likes: 520 },
    { day: 'Tue', Comments: 65, Gifts: 35, Likes: 890 },
    { day: 'Wed', Comments: 80, Gifts: 55, Likes: 1150 },
    { day: 'Thu', Comments: 120, Gifts: 95, Likes: 1280 },
    { day: 'Fri', Comments: 145, Gifts: 120, Likes: 1420 },
    { day: 'Sat', Comments: 195, Gifts: 165, Likes: 1780 },
    { day: 'Sun', Comments: 170, Gifts: 140, Likes: 1520 },
  ]

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'platform', label: 'Platform Breakdown' },
    { id: 'streamers', label: 'Top Streamers' },
    { id: 'template', label: 'Template Usage' },
    { id: 'engagement', label: 'Engagement' },
  ]

  return (
    <div className="relative bg-white dark:bg-dark-surface rounded-xl shadow-lg border border-gray-200 dark:border-dark-border p-5 min-w-0 overflow-hidden">
        {/* Coming Soon Overlay */}
        <div className="absolute inset-0 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm z-10 flex items-center justify-center rounded-xl">
          <div className="text-center px-6">
            <BarChart3 className="w-12 h-12 mx-auto mb-4 text-gray-400 dark:text-gray-500" strokeWidth={1.5} />
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Coming Soon
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Game analytics and insights are on the way
            </p>
          </div>
        </div>
      
      {/* Header with Title, Badges, and Time Range */}
      <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-xl font-bold text-gray-900 dark:text-dark-text">Game overview</h2>
          
          {/* Achievement Badges */}
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-400">
              <Trophy className="w-3.5 h-3.5" />
              #2 in Arcade
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400">
              <Star className="w-3.5 h-3.5" />
              Top 10 Overall
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400">
              <TrendingUp className="w-3.5 h-3.5" />
              Trending
            </span>
          </div>
        </div>

        {/* Time Range Dropdown */}
        <button className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-dark-surface border border-gray-300 dark:border-dark-border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
          Last 7 days
          <ChevronDown className="w-4 h-4" />
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        {/* Total Views */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
              <Eye className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
            <span className="px-2 py-0.5 bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 text-xs font-semibold rounded">+14%</span>
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-dark-text mb-0.5">126.4K</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Total Views</div>
        </div>

        {/* Avg. Engagement */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <div className="w-8 h-8 bg-pink-100 dark:bg-pink-900/30 rounded-lg flex items-center justify-center">
              <Heart className="w-4 h-4 text-pink-600 dark:text-pink-400" />
            </div>
            <span className="px-2 py-0.5 bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 text-xs font-semibold rounded">+14%</span>
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-dark-text mb-0.5">9.1%</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Avg. Engagement</div>
        </div>

        {/* Total Streams */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
              <Target className="w-4 h-4 text-orange-600 dark:text-orange-400" />
            </div>
            <span className="px-2 py-0.5 bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 text-xs font-semibold rounded">+14%</span>
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-dark-text mb-0.5">579</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Total Streams</div>
        </div>

        {/* Template Uses */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
              <Copy className="w-4 h-4 text-purple-600 dark:text-purple-400" />
            </div>
            <span className="px-2 py-0.5 bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 text-xs font-semibold rounded">+14%</span>
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-dark-text mb-0.5">142</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Template Uses</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-5 border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => tab.id === 'overview' && setActiveTab(tab.id)}
            className={`px-4 py-2 text-xs font-semibold transition-all duration-200 border-b-2 whitespace-nowrap ${
              activeTab === tab.id
                ? 'border-gray-900 dark:border-gray-100 text-gray-900 dark:text-gray-100'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 opacity-50'
            }`}
            disabled={tab.id !== 'overview'}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Charts Section */}
      {activeTab === 'overview' && (
        <div className="space-y-5">
          {/* Views by Platform Chart */}
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-dark-text mb-3">
              Views by Platform (Last 7 Days)
            </h3>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={platformViewsData}>
                <defs>
                  <linearGradient id="colorTikTok" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#00f2ea" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#00f2ea" stopOpacity={0.1}/>
                  </linearGradient>
                  <linearGradient id="colorTwitch" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#9146ff" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#9146ff" stopOpacity={0.1}/>
                  </linearGradient>
                  <linearGradient id="colorYouTube" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ff0000" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#ff0000" stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="day" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip />
                <Area type="monotone" dataKey="YouTube" stackId="1" stroke="#ff0000" fill="url(#colorYouTube)" />
                <Area type="monotone" dataKey="Twitch" stackId="1" stroke="#9146ff" fill="url(#colorTwitch)" />
                <Area type="monotone" dataKey="TikTok" stackId="1" stroke="#00f2ea" fill="url(#colorTikTok)" />
              </AreaChart>
            </ResponsiveContainer>
            <div className="flex items-center justify-center gap-4 mt-3">
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-cyan-400"></div>
                <span className="text-xs text-gray-600 dark:text-gray-400">TikTok</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-purple-500"></div>
                <span className="text-xs text-gray-600 dark:text-gray-400">Twitch</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-red-500"></div>
                <span className="text-xs text-gray-600 dark:text-gray-400">YouTube</span>
              </div>
            </div>
          </div>

          {/* Two Charts Side by Side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Peak Viewing Hours Chart */}
            <div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-dark-text mb-3">
                Peak Viewing Hours
              </h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={peakHoursData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="time" stroke="#6b7280" />
                  <YAxis stroke="#6b7280" />
                  <Tooltip />
                  <Bar dataKey="viewers" fill="#fb923c" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Engagement Trends Chart */}
            <div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-dark-text mb-3">
                Engagement Trends
              </h3>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={engagementData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="day" stroke="#6b7280" />
                  <YAxis stroke="#6b7280" />
                  <Tooltip />
                  <Line type="monotone" dataKey="Comments" stroke="#00f2ea" strokeWidth={2} dot={{ fill: '#00f2ea', r: 3 }} />
                  <Line type="monotone" dataKey="Gifts" stroke="#fb923c" strokeWidth={2} dot={{ fill: '#fb923c', r: 3 }} />
                  <Line type="monotone" dataKey="Likes" stroke="#ec4899" strokeWidth={2} dot={{ fill: '#ec4899', r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
              <div className="flex items-center justify-center gap-4 mt-3">
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-cyan-400"></div>
                  <span className="text-xs text-gray-600 dark:text-gray-400">Comments</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-orange-400"></div>
                  <span className="text-xs text-gray-600 dark:text-gray-400">Gifts</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-pink-500"></div>
                  <span className="text-xs text-gray-600 dark:text-gray-400">Likes</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Helper function to get tag styling
const getTagStyle = (tag: string, index: number) => {
  const tagLower = tag.toLowerCase()
  
  // Define tag styles with icons
  const tagStyles: Record<string, { icon: any, bgColor: string, textColor: string, label: string }> = {
    'action': { icon: Zap, bgColor: 'bg-orange-100 dark:bg-orange-900/30', textColor: 'text-orange-700 dark:text-orange-400', label: 'Action' },
    'puzzle': { icon: Puzzle, bgColor: 'bg-purple-100 dark:bg-purple-900/30', textColor: 'text-purple-700 dark:text-purple-400', label: 'Puzzle' },
    'strategy': { icon: Target, bgColor: 'bg-blue-100 dark:bg-blue-900/30', textColor: 'text-blue-700 dark:text-blue-400', label: 'Strategy' },
    'arcade': { icon: Trophy, bgColor: 'bg-yellow-100 dark:bg-yellow-900/30', textColor: 'text-yellow-700 dark:text-yellow-400', label: 'Arcade' },
    'adventure': { icon: Swords, bgColor: 'bg-red-100 dark:bg-red-900/30', textColor: 'text-red-700 dark:text-red-400', label: 'Adventure' },
    'casual': { icon: Sparkles, bgColor: 'bg-pink-100 dark:bg-pink-900/30', textColor: 'text-pink-700 dark:text-pink-400', label: 'Casual' },
  }
  
  // Find matching style or use default
  const matchedKey = Object.keys(tagStyles).find(key => tagLower.includes(key))
  if (matchedKey) {
    return tagStyles[matchedKey]
  }
  
  // Default colors cycle
  const defaultStyles = [
    { icon: Gamepad2, bgColor: 'bg-gray-100 dark:bg-gray-800', textColor: 'text-gray-700 dark:text-gray-400', label: tag },
    { icon: Sparkles, bgColor: 'bg-teal-100 dark:bg-teal-900/30', textColor: 'text-teal-700 dark:text-teal-400', label: tag },
    { icon: Target, bgColor: 'bg-indigo-100 dark:bg-indigo-900/30', textColor: 'text-indigo-700 dark:text-indigo-400', label: tag },
  ]
  
  return { ...defaultStyles[index % defaultStyles.length], label: tag.charAt(0).toUpperCase() + tag.slice(1) }
}

// Version Control Section Component
const VersionControlSection = ({ 
  project: _project, 
  versions: rawVersions,
  isLoadingVersions,
  onPublishVersion
}: { 
  project: Game,
  versions: any[],
  isLoadingVersions: boolean,
  onPublishVersion: (versionId: string, bundleVersion: number) => void
}) => {
  // Format time ago
  const formatTimeAgo = (dateString: string) => {
    if (!dateString) return 'Unknown'
    
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return 'Invalid'
    
    const now = new Date()
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)
    
    if (diffInSeconds < 60) return 'just now'
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`
    const days = Math.floor(diffInSeconds / 86400)
    if (diffInSeconds < 2592000) return `${days} day${days === 1 ? '' : 's'} ago`
    const months = Math.floor(diffInSeconds / 2592000)
    if (diffInSeconds < 31536000) return `${months} month${months === 1 ? '' : 's'} ago`
    return date.toLocaleDateString()
  }

  // Transform real versions to the format we need
  const versions = rawVersions.map(v => ({
    id: v.id,
    version: v.version_number,
    bundleVersion: v.bundle_version,
    timeAgo: formatTimeAgo(v.created_at || v.uploaded_at),
    description: v.changelog || v.upload_notes || 'No description',
    isLive: v.is_current || false,
    isPending: v.upload_status === 'uploading' || v.upload_status === 'partial',
    authorName: v.uploaded_by?.display_name || v.uploaded_by?.username || 'Unknown',
    authorImage: v.uploaded_by?.image_url
  }))

  // Default to the live version
  const liveVersionIndex = versions.findIndex(v => v.isLive)
  const [selectedVersion, setSelectedVersion] = useState<number>(liveVersionIndex >= 0 ? liveVersionIndex : 0)
  const [scrollPosition, setScrollPosition] = useState(0)

  // Reverse versions to show oldest to newest (left to right)
  const reversedVersions = [...versions].reverse()
  
  // Find the live version index in reversed array
  const liveVersionIndexReversed = reversedVersions.findIndex(v => v.isLive)
  
  // Ref for the timeline container to auto-scroll
  const timelineRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to the last (most recent) version on mount
  useEffect(() => {
    if (timelineRef.current) {
      timelineRef.current.scrollLeft = timelineRef.current.scrollWidth
    }
  }, [])

  // Show loading state
  if (isLoadingVersions) {
    return (
      <div className="bg-white dark:bg-dark-surface rounded-xl shadow-lg border border-gray-200 dark:border-dark-border p-5 min-w-0 overflow-hidden">
        <div className="mb-5">
          <h2 className="text-xl font-bold text-gray-900 dark:text-dark-text">Versions</h2>
        </div>
        <SmoothLoadingSpinner 
          isLoading={true} 
          size="md" 
          message="Loading versions..." 
          className="py-12" 
          delay={0}
        />
      </div>
    )
  }

  // Show empty state
  if (versions.length === 0) {
    return (
      <div className="bg-white dark:bg-dark-surface rounded-xl shadow-lg border border-gray-200 dark:border-dark-border p-5 min-w-0 overflow-hidden">
        <div className="mb-5">
          <h2 className="text-xl font-bold text-gray-900 dark:text-dark-text">Versions</h2>
        </div>
        <div className="text-center py-12">
          <GitBranch className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-dark-text mb-2">
            No versions yet
          </h3>
          <p className="text-gray-500 dark:text-dark-text-muted">
            Versions will appear here once you publish your project.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white dark:bg-dark-surface rounded-xl shadow-lg border border-gray-200 dark:border-dark-border p-5 overflow-hidden">
      {/* Header */}
      <div className="mb-5">
        <h2 className="text-xl font-bold text-gray-900 dark:text-dark-text">Versions</h2>
      </div>

      {/* Timeline with gradient overlay */}
      <div className="relative min-w-0">
        {/* Left fade gradient overlay - shows when scrolled */}
        {scrollPosition > 0 && (
          <div 
            className="absolute left-0 top-0 bottom-0 w-32 pointer-events-none z-20 bg-gradient-to-r from-white dark:from-dark-surface to-transparent"
          />
        )}
        
        <div 
          ref={timelineRef} 
          className="mb-5 overflow-x-auto pb-28 pt-4 px-4"
          onScroll={(e) => {
            const target = e.currentTarget;
            setScrollPosition(target.scrollLeft);
          }}
        >
          <div className="flex items-center gap-0 max-w-0 px-8">
          {reversedVersions.map((version, index) => {
            const originalIndex = versions.length - 1 - index
            const isLive = version.isLive
            const isPending = version.isPending
            
            return (
            <div key={index} className="flex items-center">
              {/* Version Node */}
              <div 
                className="relative cursor-pointer transition-all duration-200"
                onClick={() => setSelectedVersion(originalIndex)}
              >
                {/* Circle with Icon - stays on the line */}
                <div 
                  className={`relative w-12 h-12 rounded-full flex items-center justify-center transition-all duration-200 ${
                    selectedVersion === originalIndex ? 'scale-110' : ''
                  }`}
                >
                  {/* Gradient border when selected */}
                  {selectedVersion === originalIndex && (
                    <div className="absolute inset-0 rounded-full p-[2px]" style={{ background: LINE_GRADIENT }}>
                      <div className="w-full h-full rounded-full bg-white dark:bg-dark-surface" />
                    </div>
                  )}
                  
                  {/* Icon container */}
                  <div 
                    className={`relative z-10 w-full h-full rounded-full flex items-center justify-center ${
                      isPending ? 'bg-gray-100 dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600' : 
                      isLive ? '' : 
                      selectedVersion === originalIndex ? '' : 'border-2 border-gray-300 dark:border-gray-600'
                    }`}
                    style={isLive && !isPending ? { 
                      background: FILL_GRADIENT 
                    } : undefined}
                  >
                    {isPending ? (
                      <Clock className="w-6 h-6 text-gray-600 dark:text-gray-400" />
                    ) : (
                      <CheckCircle className={`w-6 h-6 ${isLive ? 'text-white' : 'text-gray-400 dark:text-gray-500'}`} />
                    )}
                  </div>
                </div>

                {/* Version Info - positioned below without background */}
                <div className="absolute top-16 left-1/2 -translate-x-1/2 text-center transition-all duration-200 whitespace-nowrap">
                  <div className="text-sm">
                    {selectedVersion === originalIndex ? (
                      <>
                        <span className="font-normal" style={{ 
                          background: LINE_GRADIENT,
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text'
                        }}>
                          Version{' '}
                        </span>
                        <span className="text-lg" style={{ color: '#FF00CF' }}>
                          {index + 1}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="font-normal text-gray-600 dark:text-gray-400">Version </span>
                        <span className="text-lg text-gray-900 dark:text-dark-text">{index + 1}</span>
                      </>
                    )}
                  </div>
                  {isLive && (
                    <div className="text-xs font-semibold mt-1" style={{ 
                      background: FILL_GRADIENT,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text'
                    }}>
                      Currently LIVE
                    </div>
                  )}
                  {!isLive && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {version.timeAgo}
                    </div>
                  )}
                </div>
              </div>

              {/* Connecting Line */}
              {index < reversedVersions.length - 1 && (
                <div 
                  className="h-0.5 w-32" 
                  style={{
                    background: index < liveVersionIndexReversed 
                      ? FILL_GRADIENT 
                      : '',
                    backgroundColor: '#d1d5db',
                    backgroundAttachment: 'fixed'
                  }}
                />
              )}
            </div>
          )})}
        </div>
      </div>
      </div>

      {/* Selected Version Details */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-base mb-1">
              <span className="font-normal text-gray-600 dark:text-gray-400">Version </span>
              <span className="text-lg text-gray-900 dark:text-dark-text">{versions.length - selectedVersion}</span>
            </h3>
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              <span>{versions[selectedVersion].timeAgo}</span>
              <span>• by</span>
              <div className="flex items-center gap-1.5">
                {versions[selectedVersion].authorImage ? (
                  <img 
                    src={versions[selectedVersion].authorImage} 
                    alt={versions[selectedVersion].authorName}
                    className="w-4 h-4 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-4 h-4 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                    <span className="text-[8px] font-semibold text-gray-600 dark:text-gray-400">
                      {versions[selectedVersion].authorName.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <span>{versions[selectedVersion].authorName}</span>
              </div>
            </div>
          </div>
          {versions[selectedVersion].isLive && (
            <span className="px-2.5 py-1 rounded-full text-xs font-semibold" style={{
              background: FILL_GRADIENT,
              color: 'white'
            }}>
              Currently LIVE
            </span>
          )}
        </div>

        <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
          {versions[selectedVersion].description}
        </p>

        {!versions[selectedVersion].isLive && !versions[selectedVersion].isPending && (
          <div className="flex justify-end">
            <button 
              className="px-4 py-2 text-sm font-semibold text-white rounded-lg hover:shadow-lg hover:scale-105 active:scale-95 transition-all duration-200 flex items-center gap-2"
              style={{ background: FILL_GRADIENT }}
              onClick={() => onPublishVersion(versions[selectedVersion].id, versions[selectedVersion].bundleVersion)}
            >
              <Upload className="w-4 h-4" />
              Publish Version
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

const ProjectDetailPage = () => {
  const { projectId } = useParams<{ projectId: string }>() // This is actually project_slug from URL
  const navigate = useNavigate()
  const { 
    projects, 
    isLoadingProjects, 
    fetchProjects, 
    isInitialized, 
    updateProject, 
    deleteProject,
    duplicateProject,
    exportProject,
    togglePublishStatus,
    fetchProjectDetails,
    currentProjectVersions,
    isLoadingVersions,
    versionsError,
    fetchProjectVersions,
    rollbackToVersion,
    deleteVersion,
    downloadVersion
  } = useProjectsStore()
  const { showToast } = useToastStore()
  
  const [project, setProject] = useState<Game | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isVersionHistoryModalOpen, setIsVersionHistoryModalOpen] = useState(false)
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false)
  const [confirmationModal, setConfirmationModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    action: (() => void) | null;
    isDestructive: boolean;
    requireTextConfirmation?: boolean;
    confirmationText?: string;
    confirmationPlaceholder?: string;
  }>({
    isOpen: false,
    title: '',
    message: '',
    action: null,
    isDestructive: false
  })
  const hasAttemptedLoad = useRef(false)

  const showConfirmation = (title: string, message: string, action: () => void, isDestructive: boolean = false) => {
    setConfirmationModal({
      isOpen: true,
      title,
      message,
      action,
      isDestructive
    })
  }

  const handleConfirmation = () => {
    if (confirmationModal.action) {
      confirmationModal.action()
    }
    setConfirmationModal(prev => ({ ...prev, isOpen: false }))
  }

  const handleVersionAction = async (action: VersionAction, version: any) => {
    if (!project) return
    
    try {
      console.log('🔄 Handling version action:', action, 'for version:', version.id)
      
      switch (action) {
        case 'set-current':
          showConfirmation(
            'Set Current Version',
            `Are you sure you want to set version ${version.version_number} as the current version?`,
            async () => {
              await rollbackToVersion(project.id, version.id)
              // Refresh versions after setting current
              await fetchProjectVersions(project.id)
            },
            false
          )
          break;
        case 'delete':
          showConfirmation(
            'Delete Version',
            `Are you sure you want to delete version ${version.version_number}? This action cannot be undone.`,
            async () => {
              await deleteVersion(project.id, version.id)
              // Refresh versions after deletion
              await fetchProjectVersions(project.id)
            },
            true
          )
          break;
        case 'download':
          await downloadVersion(project.id, version.id)
          break;
        default:
          console.warn('Unknown version action:', action)
      }
    } catch (error) {
      console.error('❌ Failed to handle version action:', error)
      // You might want to show a toast notification here
    }
  }

  const handleOpenVersionHistory = () => {
    if (project) {
      // Fetch latest versions when opening the modal
      fetchProjectVersions(project.id)
      setIsVersionHistoryModalOpen(true)
    }
  }

  const handleDeleteProject = async () => {
    if (!project) return
    
    setConfirmationModal({
      isOpen: true,
      title: 'Delete Project',
      message: `Are you sure you want to delete "${project.name}"? This action cannot be undone and will remove all versions and data.`,
      action: async () => {
        try {
          await deleteProject(project.id)
          // Navigate back to projects list after successful deletion
          navigate('/projects')
        } catch (error) {
          console.error('❌ Failed to delete project:', error)
          // You might want to show a toast notification here
        }
      },
      isDestructive: true,
      requireTextConfirmation: true,
      confirmationText: project.project_slug,
      confirmationPlaceholder: `Type "${project.project_slug}" to confirm`
    })
  }

  const handleDuplicateProject = async () => {
    if (!project) return
    
    try {
      // Show loading state
      console.log('🔄 Starting project duplication...')
      
      // Call the duplicate method from the store
      const duplicatedProject = await duplicateProject(project.id)
      
      // Show success message
      console.log('✅ Project duplicated successfully!')
      
      // Navigate to the new project
      navigate(`/projects/${duplicatedProject.id}`)
      
    } catch (error) {
      console.error('❌ Failed to duplicate project:', error)
      // You could show a toast notification here
    }
  }

  const handleExportProject = async () => {
    if (!project) return
    
    try {
      // Show loading state
      console.log('📤 Starting project export...')
      
      // Call the export method from the store (this will trigger the download)
      await exportProject(project.id)
      
      console.log('✅ Project export initiated successfully!')
      
    } catch (error) {
      console.error('❌ Failed to export project:', error)
      // You could show a toast notification here
    }
  }

  const handleProjectTransfer = async (targetTeamId: string) => {
    if (!project) return
    
    try {
      console.log('🔄 Starting project transfer to team:', targetTeamId)
      
      // Call the transfer API
      const updatedProject = await projectsService.transferProject(project.id, targetTeamId)
      
      // Update local state with the transferred project
      setProject(updatedProject)
      
      // Refresh projects in the store to reflect the change
      await fetchProjects()
      
      console.log('✅ Project transferred successfully!')
      
    } catch (error) {
      console.error('❌ Failed to transfer project:', error)
      throw error // Re-throw so the modal can handle the error
    }
  }

  useEffect(() => {
    const loadProject = async () => {
      console.log('🔍 Loading project details for ID:', projectId)
      
      if (!projectId) {
        console.error('❌ No project ID provided')
        navigate('/projects')
        return
      }

      // If projects are already loaded, find the project
      if (projects.length > 0) {
        const foundProject = projects.find(p => p.id === projectId)
        if (foundProject) {
          console.log('✅ Found project in store:', foundProject.name)
          setProject(foundProject)
          setIsLoading(false)
          return
        }
      }

      // If projects aren't loaded yet and we haven't attempted to load them, fetch them
      if (projects.length === 0 && !isLoadingProjects && !hasAttemptedLoad.current) {
        console.log('📡 Fetching projects to find project details...')
        hasAttemptedLoad.current = true
        await fetchProjects()
      }
      
      // Only set loading to false if we have projects or we're not loading
      if (projects.length > 0 || !isLoadingProjects) {
        setIsLoading(false)
      }
    }

    loadProject()
  }, [projectId, projects.length, isLoadingProjects, isInitialized, navigate, fetchProjects])

  // Update project when projects change - always sync with store
  useEffect(() => {
    if (projectId && projects.length > 0) {
      const foundProject = projects.find(p => p.id === projectId)
      if (foundProject) {
        console.log('✅ Updated project from store:', foundProject.name)
        setProject(foundProject)
        setIsLoading(false)
        
        // Fetch versions for this project on initial load
        if (!project) {
          fetchProjectVersions(foundProject.id)
        }
      }
    }
  }, [projectId, projects, fetchProjectVersions])

  // Handle initial project loading when no project is loaded yet
  useEffect(() => {
    if (projectId && projects.length === 0 && !isLoadingProjects && !hasAttemptedLoad.current) {
      console.log('📡 No projects in store, fetching...')
      hasAttemptedLoad.current = true
      fetchProjects()
    }
  }, [projectId, projects.length, isLoadingProjects, fetchProjects])

  if (isLoading || isLoadingProjects) {
    return (
      <div className="p-6">
        <SmoothLoadingSpinner 
          isLoading={isLoading || isLoadingProjects} 
          size="lg" 
          message="Loading project details..." 
          className="py-12" 
          delay={100}
        />
      </div>
    )
  }

  if (!project) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <GitBranch className="w-12 h-12 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-dark-text mb-2">
            Project not found
          </h3>
          <p className="text-gray-500 dark:text-dark-text-muted mb-4">
            The project you're looking for doesn't exist or you don't have access to it.
          </p>
          <Link
            to="/projects"
            className="btn btn-primary"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Projects
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 space-y-4 w-full max-w-[1000px] min-w-0 mx-auto">
      {/* Back to Games Button */}
      <Link 
        to="/projects" 
        className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors font-medium text-sm"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Games
      </Link>

      {/* Project Overview Card */}
      <div className="bg-white dark:bg-dark-surface rounded-xl shadow-lg border border-gray-200 dark:border-dark-border">
        {/* Gradient Line at Top */}
        <div className="h-1 w-full rounded-t-xl overflow-hidden" style={{ background: LINE_GRADIENT }} />
        
        {/* Cover Image */}
        <div className="relative h-48 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900 overflow-hidden">
          {project.content?.cover_image_url ? (
            <img 
              src={project.content.cover_image_url} 
              alt={`${project.name} cover`}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Gamepad2 className="w-20 h-20 text-gray-400 dark:text-gray-600" />
            </div>
          )}
        </div>

        {/* Content Section */}
        <div className="p-5 pt-4 relative">
          {/* Game Icon - Overlapping Cover */}
          <div className="absolute -top-12 left-5">
            <div className="w-24 h-24 rounded-xl shadow-xl border-4 border-white dark:border-dark-surface overflow-hidden bg-gray-50 dark:bg-gray-800/50">
              {project.content?.icon_url ? (
                <img 
                  src={project.content.icon_url} 
                  alt={`${project.name} icon`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none'
                    const parent = e.currentTarget.parentElement
                    if (parent) {
                      parent.innerHTML = '<div class="w-full h-full flex items-center justify-center"><svg class="w-12 h-12 text-gray-400" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="6" x2="10" y1="12" y2="12"/><line x1="8" x2="8" y1="10" y2="14"/><line x1="15" x2="15.01" y1="13" y2="13"/><line x1="18" x2="18.01" y1="11" y2="11"/><rect width="20" height="16" x="2" y="4" rx="2"/></svg></div>'
                    }
                  }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Gamepad2 className="w-12 h-12 text-gray-400" />
                </div>
              )}
            </div>
          </div>

          {/* Header with Title and Actions */}
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1 mt-12">
              {/* Title and Status Tag */}
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-dark-text">
                  {project.name}
                </h1>
                
                {/* Status Tag */}
                {project.status === 'active' ? (
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 text-xs font-semibold">
                    <CheckCircle className="w-3.5 h-3.5" />
                    Published
                  </div>
                ) : project.status === 'inactive' ? (
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400 text-xs font-semibold">
                    <XCircle className="w-3.5 h-3.5" />
                    Not Published
                  </div>
                ) : (
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400 text-xs font-semibold">
                    <Lock className="w-3.5 h-3.5" />
                    Private
                  </div>
                )}
              </div>

              {/* Description */}
              <p className="text-sm text-gray-600 dark:text-dark-text-muted mb-3 max-w-3xl">
                {project.description || 'Detailed analytics and performance metrics for this game'}
              </p>

              {/* Category Tags */}
              <div className="flex items-center gap-1 flex-wrap">
                {(project.content?.tags?.slice(0, 3) || [project.content?.category || 'Game']).map((tag, tagIndex) => {
                  const tagStyle = getTagStyle(tag, tagIndex)
                  const TagIcon = tagStyle.icon
                  return (
                    <span 
                      key={`tag-${tagIndex}`}
                      className={`px-2 py-1 rounded-md text-xs font-medium flex items-center gap-1 ${tagStyle.bgColor} ${tagStyle.textColor}`}
                    >
                      <TagIcon className="w-3 h-3" />
                      {tagStyle.label}
                    </span>
                  )
                })}
              </div>
            </div>

            {/* Options Button */}
            <GameOptionsDropdown
              project={project}
              onPublish={async () => {
                try {
                  console.log(`🔄 Publishing project:`, project.name)
                  await togglePublishStatus(project.id, false)
                  console.log(`✅ Project published successfully!`)
                  showToast('success', `"${project.name}" published successfully!`)
                  await fetchProjectDetails(project.id)
                } catch (error) {
                  console.error(`❌ Failed to publish project:`, error)
                  showToast('error', `Failed to publish project. Please try again.`)
                }
              }}
              onUnpublish={async () => {
                try {
                  console.log(`🔄 Unpublishing project:`, project.name)
                  await togglePublishStatus(project.id, true)
                  console.log(`✅ Project unpublished successfully!`)
                  showToast('success', `"${project.name}" unpublished successfully!`)
                  await fetchProjectDetails(project.id)
                } catch (error) {
                  console.error(`❌ Failed to unpublish project:`, error)
                  showToast('error', `Failed to unpublish project. Please try again.`)
                }
              }}
              onArchive={async () => {
                setConfirmationModal({
                  isOpen: true,
                  title: 'Archive Project',
                  message: `Are you sure you want to archive "${project.name}"? You can unarchive it later from the Archived tab.`,
                  action: async () => {
                    try {
                      await deleteProject(project.id)
                      navigate('/projects?tab=archived')
                    } catch (error) {
                      console.error('❌ Failed to archive project:', error)
                      alert('Failed to archive project. Please try again.')
                    }
                  },
                  isDestructive: false
                })
              }}
              onDuplicate={handleDuplicateProject}
              onEdit={() => setIsEditModalOpen(true)}
              onTransfer={() => setIsTransferModalOpen(true)}
              size="md"
              buttonClassName="p-2.5 bg-white dark:bg-dark-surface border border-gray-300 dark:border-dark-border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-200"
            />

          </div>

          {/* Action Row - Team Tag on Left, Buttons on Right */}
          <div className="flex items-center justify-between gap-4">
            {/* Team Tag - Only show if not personal team */}
            <TeamTag team={project.team} size="md" />
            
            {/* Action Buttons */}
            <div className="flex items-center gap-2 ml-auto">
              {/* Hidden for now - Get App Link
              <button 
                className="px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 bg-white dark:bg-dark-surface border border-gray-300 dark:border-dark-border rounded-lg hover:shadow-md hover:scale-105 active:scale-95 transition-all duration-200 flex items-center gap-2"
                onClick={() => {
                  const webplayUrl = `${import.meta.env.VITE_WEBPLAY_URL || 'https://play.beemi.app'}/games/${project.id}`;
                  navigator.clipboard.writeText(webplayUrl);
                  showToast('success', 'App link copied to clipboard!');
                }}
              >
                <Link2 className="w-4 h-4" />
                Get App Link
              </button>
              */}

              <button
                className="px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 bg-white dark:bg-dark-surface border border-gray-300 dark:border-dark-border rounded-lg hover:shadow-md hover:scale-105 active:scale-95 transition-all duration-200 flex items-center gap-2"
                onClick={() => {
                  const webplayUrl = `${import.meta.env.VITE_WEBPLAY_URL || 'https://play.beemi.app'}/games/${project.id}`;
                  navigator.clipboard.writeText(webplayUrl);
                  showToast('success', 'Game link copied to clipboard!');
                }}
              >
                <Share2 className="w-4 h-4" />
                Share
              </button>

              <button 
                className="px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 bg-white dark:bg-dark-surface border border-gray-300 dark:border-dark-border rounded-lg hover:shadow-md hover:scale-105 active:scale-95 transition-all duration-200 flex items-center gap-2"
                onClick={() => {
                  const webplayUrl = `${import.meta.env.VITE_WEBPLAY_URL || 'https://play.beemi.app'}/games/${project.id}`;
                  window.open(webplayUrl, '_blank');
                }}
              >
                <ExternalLink className="w-4 h-4" />
                Open in Web
              </button>

              <button 
                className="px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 bg-white dark:bg-dark-surface border border-gray-300 dark:border-dark-border rounded-lg hover:shadow-md hover:scale-105 active:scale-95 transition-all duration-200 flex items-center gap-2"
                onClick={async () => {
                  // Refetch project from API to get latest changes (in case edited in Editor)
                  try {
                    const freshProject = await projectsService.getProjectDetails(project.id);
                    setProject(freshProject);
                  } catch (error) {
                    console.error('Failed to refetch project:', error);
                  }
                  setIsEditModalOpen(true);
                }}
              >
                <Edit3 className="w-4 h-4" />
                Edit Details
              </button>
              
              {/* Open in Editor - Only for Studio projects (not archived) */}
              {project.is_editable_in_studio && project.status !== 'deleted' && (
                <button 
                  className="px-4 py-2 text-sm font-semibold text-white rounded-lg hover:shadow-lg hover:scale-105 active:scale-95 transition-all duration-200 flex items-center gap-2"
                  style={{ background: FILL_GRADIENT }}
                  onClick={() => navigate(`/editor/${project.id}`)}
                >
                  <Edit className="w-4 h-4" />
                  Open in Editor
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Version Control Section */}
      <VersionControlSection 
        project={project} 
        versions={currentProjectVersions}
        isLoadingVersions={isLoadingVersions}
        onPublishVersion={(versionId: string, bundleVersion: number) => {
          showConfirmation(
            'Publish Version',
            `Are you sure you want to publish Version ${bundleVersion} and make it the current live version?`,
            async () => {
              try {
                await projectsService.rollbackToVersion(project.id, versionId);
                fetchProjectVersions(project.id);
                console.log('✅ Version published successfully');
              } catch (error) {
                console.error('❌ Failed to publish version:', error);
                alert('Failed to publish version. Please try again.');
              }
            },
            false
          );
        }}
      />

      {/* Game Overview Section */}
      <GameOverviewSection project={project} />

      {/* Version History Modal */}
      <VersionHistoryModal
        isOpen={isVersionHistoryModalOpen}
        onClose={() => setIsVersionHistoryModalOpen(false)}
        projectId={project.id}
        projectName={project.name}
        versions={currentProjectVersions}
        isLoading={isLoadingVersions}
        error={versionsError}
        onActionSelect={handleVersionAction}
        onRefresh={() => fetchProjectVersions(project.id)}
      />

      {/* Edit Project Modal */}
      <ProjectEditModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        project={project}
        showGenerateButton={project.is_editable_in_studio ?? false} // Show for studio projects
        onSave={async (updates) => {
          console.log('💾 Saving project updates:', updates, 'using project UUID:', project.id)
          
          try {
            // Save the updates to the server using project UUID, not URL slug
            await updateProject(project.id, updates)
            
            console.log('✅ Project updates saved successfully')
            
            // Update local state immediately with the new data
            setProject(prev => {
              if (!prev) return null
              
              const updatedProject = {
                ...prev,
                // Preserve all existing fields, especially project_slug
                name: updates.name || prev.name,
                description: updates.description || prev.description,
                // Make sure project_slug is preserved
                project_slug: prev.project_slug,
                content: prev.content ? {
                  ...prev.content,
                  name: updates.name || prev.content.name,
                  description: updates.description || prev.content.description,
                  category: updates.category || prev.content.category,
                  tags: updates.tags || prev.content.tags,
                  play_instructions: updates.play_instructions || prev.content.play_instructions,
                  screenshot_urls: updates.screenshot_urls || prev.content.screenshot_urls,
                  age_rating: updates.age_rating || prev.content.age_rating,
                  content_warnings: updates.content_warnings || prev.content.content_warnings,
                  seo_keywords: updates.seo_keywords || prev.content.seo_keywords,
                  icon_url: updates.icon_url || prev.content.icon_url,
                  cover_image_url: updates.cover_image_url || prev.content.cover_image_url,
                } : undefined
              }
              
              console.log('🔄 Updated local project state:', {
                content_icon_url: updatedProject.content?.icon_url,
                content_cover_image_url: updatedProject.content?.cover_image_url
              })
              
              return updatedProject
            })
            
            // Close modal after successful save
            setIsEditModalOpen(false)
          } catch (error) {
            console.error('❌ Failed to save project updates:', error)
            // Don't close modal if save failed, let user try again
            throw error // Re-throw so ProjectEditModal can handle the error
          }
        }}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={() => setConfirmationModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={handleConfirmation}
        title={confirmationModal.title}
        message={confirmationModal.message}
        confirmText={confirmationModal.isDestructive ? 'Delete' : 'Confirm'}
        isDestructive={confirmationModal.isDestructive}
        requireTextConfirmation={confirmationModal.requireTextConfirmation}
        confirmationText={confirmationModal.confirmationText}
        confirmationPlaceholder={confirmationModal.confirmationPlaceholder}
      />

      {/* Project Transfer Modal */}
      <ProjectTransferModal
        isOpen={isTransferModalOpen}
        onClose={() => setIsTransferModalOpen(false)}
        projectId={project.id}
        projectName={project.name}
        projectSlug={project.project_slug}
        currentTeamId={project.team_id}
        onTransfer={handleProjectTransfer}
      />
    </div>
  )
}

export default ProjectDetailPage 